import { useState, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, TrendUp, Heartbeat } from "@phosphor-icons/react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth, API } from "../App";

const Login = () => {
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.slice(0, 9);
  };

  const handlePhoneChange = (e) => {
    setPhone(formatPhone(e.target.value));
  };

  const handleContinue = () => {
    if (phone.length !== 9) {
      toast.error("Por favor, insere um número válido (9 dígitos)");
      return;
    }
    setShowPin(true);
  };

  const handlePinChange = useCallback((index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    
    setPin(prevPin => {
      const newPin = [...prevPin];
      newPin[index] = digit;
      return newPin;
    });

    // Auto-focus next input
    if (digit && index < 3) {
      const nextInput = document.getElementById(`login-pin-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  }, []);

  const handlePinKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const prevInput = document.getElementById(`login-pin-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  }, [pin]);

  const pinValue = useMemo(() => pin.join(''), [pin]);

  const handleSubmit = async () => {
    if (pinValue.length !== 4) {
      toast.error("Por favor, insere o teu PIN de 4 dígitos");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        phone: `+244${phone}`,
        pin: pinValue
      });

      login(response.data.token, response.data.user);
      toast.success(`Bem-vindo(a) de volta, ${response.data.user.name}!`);
      
      // Go directly to dashboard if has goal and calories set
      if (response.data.user.goal && response.data.user.daily_calories) {
        navigate("/");
      } else {
        navigate("/focus");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Telefone ou PIN incorreto");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const handleChangeNumber = () => {
    setShowPin(false);
    setPin(["", "", "", ""]);
  };

  // Memoized PIN inputs
  const pinInputs = useMemo(() => (
    <div className="flex justify-center gap-3">
      {[0, 1, 2, 3].map((index) => (
        <input
          key={`login-pin-input-${index}`}
          id={`login-pin-${index}`}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          value={pin[index]}
          onChange={(e) => handlePinChange(index, e.target.value)}
          onKeyDown={(e) => handlePinKeyDown(index, e)}
          className="w-14 h-14 text-2xl text-center bg-accent text-white border-2 border-primary/30 rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
          data-testid={`pin-slot-${index}`}
          autoComplete="off"
        />
      ))}
    </div>
  ), [pin, handlePinChange, handlePinKeyDown]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-primary text-white p-4">
        <h1 className="text-xl font-bold">Nzadi Fit</h1>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          {/* Brand with Health Icon */}
          <div className="mb-8">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heartbeat size={48} className="text-primary" weight="duotone" />
            </div>
            <h1 className="text-4xl font-bold text-primary mb-2">Nzadi Fit</h1>
            <h2 className="text-2xl font-bold text-foreground">Bem-vindo(a)!</h2>
            <p className="text-muted-foreground mt-2">Entra na tua conta Nzadi Fit</p>
          </div>

          {!showPin ? (
            <div className="space-y-4">
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70" size={24} />
                <span className="absolute left-12 top-1/2 -translate-y-1/2 text-white/90 font-medium text-sm">+244</span>
                <input
                  type="tel"
                  placeholder="9xx xxx xxx"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="w-full bg-accent text-white placeholder:text-white/70 border-2 border-transparent focus:border-primary rounded-2xl pl-[5.5rem] pr-6 py-4 text-lg outline-none transition-all"
                  maxLength={9}
                  data-testid="phone-input"
                />
              </div>

              <button
                onClick={handleContinue}
                className="btn-primary w-full"
                data-testid="continue-button"
              >
                Continuar
              </button>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <div>
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <TrendUp size={32} className="text-primary" weight="duotone" />
                </div>
                <p className="text-muted-foreground">Insere o teu PIN</p>
              </div>

              {pinInputs}

              <button
                onClick={handleSubmit}
                disabled={loading || pinValue.length !== 4}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="login-button"
              >
                {loading ? "A entrar..." : "Entrar"}
              </button>

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleChangeNumber}
                  className="text-primary font-semibold hover:underline"
                  data-testid="change-number-button"
                >
                  Mudar número
                </button>
                <button
                  onClick={handleForgotPassword}
                  className="text-muted-foreground hover:text-primary transition-colors"
                  data-testid="forgot-password-button"
                >
                  Esqueci a minha senha
                </button>
              </div>
            </motion.div>
          )}

          <p className="mt-6 text-muted-foreground">
            Não tens conta?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="register-link">
              Criar conta
            </Link>
          </p>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="p-4 text-center text-muted-foreground text-sm">
        Nzadi Fit © 2026 – Saúde em Angola 🇦🇴
      </footer>
    </div>
  );
};

export default Login;
